/**
 * Replay compatibility gates per game mode.
 * When a mode's simulation changes in a breaking way, bump the minimum
 * compatible game version for that mode. Old replays stay stored and the
 * associated numeric record is untouched — only playback is disabled.
 */

/** SemVer compare: -1 if a<b, 0 if equal, 1 if a>b. */
export function compareSemver(a: string, b: string): number {
  const pa = String(a || "0")
    .split(".")
    .map((x) => parseInt(x.replace(/^\D+/, ""), 10) || 0);
  const pb = String(b || "0")
    .split(".")
    .map((x) => parseInt(x.replace(/^\D+/, ""), 10) || 0);
  const n = Math.max(pa.length, pb.length);
  for (let i = 0; i < n; i++) {
    const av = pa[i] || 0;
    const bv = pb[i] || 0;
    if (av < bv) return -1;
    if (av > bv) return 1;
  }
  return 0;
}

/**
 * Minimum game version (inclusive) whose replays remain playable for each mode.
 * Modes omitted default to "0.0.0" (all versions playable until a break is declared).
 *
 * Example future break:
 *   normal: "1.2.0"  → replays recorded on <1.2.0 show as unavailable
 */
export const REPLAY_MIN_COMPATIBLE_VERSION: Record<string, string> = {
  // All modes start compatible from first replay-enabled release.
};

export const REPLAY_DEFAULT_MIN_VERSION = "0.0.0";

export function minCompatibleVersionForMode(mode: string): string {
  return REPLAY_MIN_COMPATIBLE_VERSION[mode] || REPLAY_DEFAULT_MIN_VERSION;
}

/** True when a replay recorded at `recordedVersion` can still be played for `mode`. */
export function isReplayCompatible(mode: string, recordedVersion: string | null | undefined): boolean {
  if (!recordedVersion || !String(recordedVersion).trim()) return false;
  const min = minCompatibleVersionForMode(mode);
  return compareSemver(String(recordedVersion).trim(), min) >= 0;
}
